import { makeAutoObservable, runInAction } from "mobx";
import { OrderDetailObserver } from "@/observers/OrderDetailObserver";
import { clientService } from "@/services/ClientService";
import { companyLinkService } from "@/services/CompanyLinkService";
import { companyService } from "@/services/CompanyService";
import { OrderStatus } from "@/types/order/order.common";
import { onB2BError } from "@/utils/b2bErrors";
import { B2B_ENABLED } from "@/utils/featureFlags";
import { onError } from "@/utils/onError";
/**
 * MobX store for the order detail page data layer.
 * Owns an OrderDetailObserver for per-order realtime updates.
 *
 * UI (Wave 3/4) reads:  details, statusHistory, chargingSessions, isLoading
 * UI calls:             load(orderId), startRealtime(orderId), stopRealtime()
 */
export class OrderDetailStore {
    constructor(service) {
        this.service = service;
        this.details = null;
        this.statusHistory = [];
        this.chargingSessions = [];
        this.client = null;
        this.isLoading = false;
        /**
         * FR-007 (UC-102, `PayerCard`): компания-плательщик и подпись сотрудника. Грузятся только у корп-заказа
         * при `B2B_ENABLED`; баланс и лимит сюда не входят — `v_corp_balances` из карточки не запрашивается (D14).
         */
        this.payerCompany = null;
        this.payerEmployeeLabel = null;
        this.isPayerLoading = false;
        this.observer = new OrderDetailObserver();
        makeAutoObservable(this);
    }
    /**
     * BUG-3 guard: true when the order is COMPLETED but at least one
     * charging session is still active (no end_time or status active/charging).
     */
    get isSessionActiveAfterCompletion() {
        if (this.details?.status !== OrderStatus.COMPLETED) {
            return false;
        }
        return this.chargingSessions.some((s) => s.endTime === null ||
            (typeof s.status === "string" &&
                ["active", "charging"].includes(s.status.toLowerCase())));
    }
    /**
     * Load details + status history + charging sessions for an order.
     * Errors are displayed via notification (onError) and are non-fatal to the page.
     */
    async load(orderId) {
        this.isLoading = true;
        try {
            const [details, statusHistory, chargingSessions] = await Promise.all([
                this.service.getOrderDetails(orderId),
                this.service.getStatusHistory(orderId),
                this.service.getActiveChargingSessions(orderId),
            ]);
            runInAction(() => {
                this.details = details;
                this.statusHistory = statusHistory;
                this.chargingSessions = chargingSessions;
            });
            // FR-007: плательщик неизменяем (CP008) — грузится один раз, в refetch не повторяется
            if (details)
                void this.loadPayer(details);
            // Load client independently — non-fatal if it fails
            if (details?.idClient) {
                try {
                    const clients = await clientService.getClientsByIds([details.idClient]);
                    runInAction(() => {
                        this.client = clients[0] ?? null;
                    });
                }
                catch (clientError) {
                    onError(clientError, "Не удалось загрузить данные клиента");
                }
            }
        }
        catch (error) {
            onError(error, "Не удалось загрузить детали заказа");
        }
        finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
    /**
     * FR-007: данные `PayerCard` корп-заказа. Привязка ищется **без фильтра состояния** — у заказа она может быть
     * уже истёкшей или заблокированной, а подпись сотрудника должна остаться. Ошибка не мешает остальной карточке.
     */
    async loadPayer(details) {
        this.payerCompany = null;
        this.payerEmployeeLabel = null;
        if (!B2B_ENABLED || !details.isCorporate || !details.corpCompanyId)
            return;
        const { id, corpCompanyId, corpLinkId, idClient } = details;
        this.isPayerLoading = true;
        try {
            const [company, links] = await Promise.all([
                companyService.getCompany(corpCompanyId),
                idClient && corpLinkId ? companyLinkService.getClientLinks(idClient) : Promise.resolve([]),
            ]);
            runInAction(() => {
                // Пока шёл запрос, могли открыть другой заказ — чужие данные не показываем
                if (this.details?.id !== id)
                    return;
                this.payerCompany = company;
                this.payerEmployeeLabel = links.find((link) => link.linkId === corpLinkId)?.employeeLabel ?? null;
            });
        }
        catch (error) {
            onB2BError(error, "Не удалось загрузить данные компании-плательщика");
        }
        finally {
            runInAction(() => {
                this.isPayerLoading = false;
            });
        }
    }
    /**
     * Re-fetch details only (triggered by realtime UPDATE events).
     * History/sessions are re-fetched too in case they changed.
     */
    async refetch(orderId) {
        try {
            const [details, statusHistory, chargingSessions] = await Promise.all([
                this.service.getOrderDetails(orderId),
                this.service.getStatusHistory(orderId),
                this.service.getActiveChargingSessions(orderId),
            ]);
            runInAction(() => {
                this.details = details;
                this.statusHistory = statusHistory;
                this.chargingSessions = chargingSessions;
            });
        }
        catch (error) {
            onError(error, "Не удалось обновить детали заказа");
        }
    }
    /**
     * Start the realtime subscription for a given order.
     * On UPDATE → refetch full row.
     */
    startRealtime(orderId) {
        this.observer.start(orderId, () => this.refetch(orderId));
    }
    /**
     * Stop the realtime subscription (call on unmount / orderId change cleanup).
     */
    stopRealtime() {
        this.observer.stop();
    }
    /**
     * Full cleanup — stop realtime and clear all state.
     * Called when navigating away from the order page.
     */
    dispose() {
        this.stopRealtime();
        runInAction(() => {
            this.details = null;
            this.statusHistory = [];
            this.chargingSessions = [];
            this.client = null;
            this.isLoading = false;
            this.payerCompany = null;
            this.payerEmployeeLabel = null;
            this.isPayerLoading = false;
        });
    }
}
